import { stText } from "../../i18n/streamingPagesPresentation";
/**
 * Media Requests — the shaping layer behind the poster-first surface.
 *
 * The table needed a request to be a row. The canvas needs it to be a title:
 * something with artwork, a place on a shelf, and a relationship to what is
 * already in the library. Everything here is pure, so the components stay
 * about layout.
 */
import type { MediaRequest, MediaRequestStatus } from "../../hooks/useMediaRequests";

// ---------------------------------------------------------------------------
// Enriched fields
// ---------------------------------------------------------------------------

/**
 * The enriched wire shape, mirroring the request payload the server publishes.
 *
 * Everything past the base contract is optional and best-effort: a provider
 * that supplies less simply produces less, and no consumer may substitute a
 * default for something the provider declined to say. "No progress reported"
 * and "0% complete" are different claims, and only one of them is ours to make.
 */
export type RequestDownload = {
  title: string;
  status: string;
  size?: number;
  sizeLeft?: number;
  progressPercent?: number;
  estimatedCompletionTime?: string;
  timeLeft?: string;
  episode?: { seasonNumber: number; episodeNumber: number };
};

export type RequestEpisode = {
  episodeNumber: number;
  title?: string;
  airDate?: string;
  available: boolean;
  approved: boolean;
  requested: boolean;
};

export type RequestSeason = {
  seasonNumber: number;
  status: MediaRequestStatus;
  availability?: string;
  episodes?: RequestEpisode[];
};

export type RequestCredit = {
  name: string;
  character?: string;
  /** Metadata-provider-relative path — NOT a renderable URL. */
  profilePath?: string;
};

export type RequestMetadata = {
  title?: string;
  originalTitle?: string;
  overview?: string;
  tagline?: string;
  releaseDate?: string;
  genres?: string[];
  runtimeMinutes?: number;
  voteAverage?: number;
  voteCount?: number;
  certification?: string;
  status?: string;
  posterPath?: string;
  backdropPath?: string;
  cast?: RequestCredit[];
  directors?: string[];
  writers?: string[];
};

/**
 * Where a request landed in the library, once it did.
 *
 * This is what closes the loop between the two surfaces: with it, a request
 * links through to the canonical item page; without it, the request is only
 * ever a request. Only exact id matches are emitted, never a title guess.
 */
export type RequestLibraryMatch = {
  serverType: "emby" | "jellyfin" | "plex";
  itemId: string;
  matchedOn: "tmdb" | "tvdb" | "imdb";
  name?: string;
};

export type EnrichedMediaRequest = MediaRequest & {
  overview?: string;
  releaseDate?: string;
  backdropPath?: string;
  availability?: string;
  upstreamStatus?: string;
  seasons?: RequestSeason[];
  downloads?: RequestDownload[];
  metadata?: RequestMetadata;
  libraryMatch?: RequestLibraryMatch;
};

// ---------------------------------------------------------------------------
// Reading the enriched payload
// ---------------------------------------------------------------------------

/**
 * The canonical metadata block is richer than the fields carried on the request
 * itself, so it wins where both exist — but the request's own copy is the
 * fallback rather than nothing, because the metadata cache is warmed lazily and
 * is legitimately absent on a cold read.
 */
export const requestOverview = (request: EnrichedMediaRequest): string | undefined =>
  request.metadata?.overview ?? request.overview;

export const requestGenres = (request: EnrichedMediaRequest): string[] | undefined =>
  request.metadata?.genres;

export const requestRuntime = (request: EnrichedMediaRequest): number | undefined =>
  request.metadata?.runtimeMinutes;

export const requestRating = (request: EnrichedMediaRequest): number | undefined =>
  request.metadata?.voteAverage;

export const requestCast = (request: EnrichedMediaRequest): RequestCredit[] | undefined =>
  request.metadata?.cast;

/** The release year, when a parseable date was supplied. Never guessed. */
export const requestYear = (request: EnrichedMediaRequest): number | undefined => {
  const raw = request.releaseDate ?? request.metadata?.releaseDate;
  if (!raw) return undefined;
  const year = Number(raw.slice(0, 4));
  return Number.isFinite(year) && year > 1800 ? year : undefined;
};

/**
 * One download to speak for the request.
 *
 * A series request can have several files in flight at once, and a card has
 * room for one line. The furthest-along is the honest single answer: it is what
 * the request's own progress is bounded by, and it is the one that will finish
 * first. Entries with no reported percentage cannot be compared, so they only
 * supply a status when nothing better exists.
 */
export const requestDownload = (
  request: EnrichedMediaRequest,
): { percent?: number; status: string } | undefined => {
  const downloads = request.downloads;
  if (!downloads || downloads.length === 0) return undefined;

  let best: RequestDownload | undefined;
  for (const download of downloads) {
    if (download.progressPercent === undefined) continue;
    if (best?.progressPercent === undefined || download.progressPercent > best.progressPercent) {
      best = download;
    }
  }

  const chosen = best ?? downloads[0];
  if (!chosen) return undefined;

  return {
    ...(chosen.progressPercent !== undefined ? { percent: chosen.progressPercent } : {}),
    status: chosen.status,
  };
};

/**
 * How many episodes a season covers, counted rather than asserted.
 *
 * The provider sends the episodes themselves, so the count is derived from what
 * actually arrived — a season it described without listing reports no count at
 * all rather than zero.
 */
export const seasonEpisodeCount = (season: RequestSeason): number | undefined =>
  season.episodes?.length;

// ---------------------------------------------------------------------------
// Artwork
// ---------------------------------------------------------------------------

/** True when nothing identifies this title to an art provider. */
export const hasArtIdentifiers = (request: MediaRequest): boolean =>
  request.tmdbId !== undefined || request.tvdbId !== undefined;

/**
 * Artwork for a request, from the endpoint that exists for exactly this.
 *
 * Deliberately NOT the cover-art image proxy. That proxy answers a different
 * question — whether canonical artwork should REPLACE what a media server
 * already holds for a library item — and it is off by default, because that is
 * a presentation preference an operator is entitled to decline. A requested
 * title is not in the library and has no media-server artwork to replace, so
 * its metadata provider is not the preferred source but the only one. Routing
 * requests through the gated proxy is what made every poster blank.
 *
 * Two source shapes, in preference order:
 *
 * - `path`, which Ombi supplies directly on the row, and which a Seerr row also
 *   carries once its poster has resolved — either way it skips a lookup
 * - `tmdbId`, the fallback for a row whose poster path has not resolved
 *
 * Every failure yields a transparent pixel rather than an error status, so a
 * URL from here is always safe to hand to an `<img>`.
 */
const artworkUrl = (
  request: EnrichedMediaRequest,
  kind: "poster" | "backdrop",
  maxWidth: number,
): string | null => {
  const path =
    kind === "poster"
      ? (request.posterPath ?? request.metadata?.posterPath)
      : (request.backdropPath ?? request.metadata?.backdropPath);

  const params = new URLSearchParams({ kind, maxWidth: String(maxWidth) });

  if (path) {
    params.set("path", path);
  } else if (request.tmdbId !== undefined) {
    params.set("tmdbId", String(request.tmdbId));
    params.set("mediaType", request.mediaType);
  } else {
    // Neither source. Returning null lets the card go straight to its composed
    // plate rather than spending a request to be handed a blank pixel.
    return null;
  }

  return `/api/media-requests/artwork?${params.toString()}`;
};

export const requestPosterUrl = (
  request: EnrichedMediaRequest,
  maxWidth = 342,
): string | null => artworkUrl(request, "poster", maxWidth);

export const requestBackdropUrl = (
  request: EnrichedMediaRequest,
  maxWidth = 1280,
): string | null => artworkUrl(request, "backdrop", maxWidth);

/**
 * A cast portrait, from the same ungated endpoint the posters use.
 *
 * Kept separate from `artworkUrl` because a portrait is identified differently:
 * a poster can be resolved from the title's id when the provider supplies no
 * path, but a person's portrait cannot — the title's id says nothing about who
 * appears in it. So an absent `profilePath` has no second source, and returning
 * null sends the card to its icon rather than spending a request to be handed a
 * blank pixel.
 *
 * The default width is the shelf's own: portraits render in an 80px circle, and
 * TMDB's portrait sizes step from 185px straight to a ~421px-wide variant, so
 * anything larger fetches several times the pixels the circle can show.
 */
export const requestPortraitUrl = (
  profilePath: string | undefined,
  maxWidth = 185,
): string | null => {
  if (!profilePath) return null;
  const params = new URLSearchParams({
    kind: "profile",
    path: profilePath,
    maxWidth: String(maxWidth),
  });
  return `/api/media-requests/artwork?${params.toString()}`;
};

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/**
 * The canonical detail route for a request.
 *
 * The segments are exactly what the detail read needs, so a link that survives
 * a reload carries everything required to fetch the request fresh — nothing
 * here depends on the list that produced it.
 *
 * Every segment is encoded: a requester-controlled id or an instance owner's
 * name can contain characters that would otherwise change the path's shape.
 */
export const requestDetailPath = (request: MediaRequest): string =>
  [
    "/streaming/dashboard/control-center/requests",
    encodeURIComponent(request.providerId),
    encodeURIComponent(request.ownerUsername),
    encodeURIComponent(request.mediaType),
    encodeURIComponent(request.requestId),
  ].join("/");

// ---------------------------------------------------------------------------
// Shelves
// ---------------------------------------------------------------------------

export type RequestShelfId =
  | "awaiting"
  | "downloading"
  | "approved"
  | "available"
  | "fulfilled"
  | "declined";

export type RequestShelfDefinition = {
  id: RequestShelfId;
  label: string;
  /** Says what the shelf is for when it is the only thing on screen. */
  description: string;
  icon: string;
};

/**
 * Shelf order puts the work first.
 *
 * Someone opens this surface to decide things, so what needs a decision leads
 * and the archive trails. Anything already decided is reference, not a task.
 */
export const REQUEST_SHELVES: readonly RequestShelfDefinition[] = [
  {
    id: "awaiting",
    get label() { return stText("residualShelfAwaiting"); },
    description: "Requested and waiting on an approval or a denial.",
    icon: "lucide:inbox",
  },
  {
    id: "downloading",
    get label() { return stText("residualShelfDownloading"); },
    description: "Approved and currently being fetched.",
    icon: "lucide:download",
  },
  {
    id: "approved",
    get label() { return stText("residualShelfApproved"); },
    description: "Approved and on the way, with nothing downloading yet.",
    icon: "lucide:check-check",
  },
  {
    id: "available",
    get label() { return stText("residualShelfAvailable"); },
    description: "Landed in the library and ready to watch.",
    icon: "lucide:circle-check-big",
  },
  {
    id: "fulfilled",
    get label() { return stText("residualShelfFulfilled"); },
    description: "Earlier requests, kept for the record.",
    icon: "lucide:history",
  },
  {
    id: "declined",
    get label() { return stText("residualShelfDeclined"); },
    description: "Denied, kept for the record.",
    icon: "lucide:circle-slash",
  },
] as const;

/**
 * Which shelf a request belongs on.
 *
 * A request is on exactly one shelf, so the shelves partition the list and the
 * counts add up to the total. "Downloading" is a refinement of approved rather
 * than a status of its own, so it is tested first and falls back to approved
 * when no download is reported.
 *
 * `failed` and `unknown` sit with the awaiting group deliberately: both mean a
 * human still has to look, and burying them under a status nobody scans for
 * would let them sit indefinitely.
 *
 * Request-history rows (`hist:`-keyed — from the request manager's log, whether
 * backfilled from an export or observed while polling) get their OWN end-state
 * shelf, whether or not each currently matches the library. They are a read-only
 * archive of earlier requests, so they never mix into the live queues — and a
 * matched one must not join the live "Now available" shelf, whose promise is
 * about live requests, while an unmatched one must not sit in the actionable
 * queue or claim a library-landed state its own card denies. The shelf groups by
 * provenance; each card's badge carries its own state.
 */
export const shelfForRequest = (request: EnrichedMediaRequest): RequestShelfId => {
  if (request.key.startsWith("hist:")) return "fulfilled";
  switch (request.status) {
    case "approved":
      return requestDownload(request) ? "downloading" : "approved";
    case "available":
      return "available";
    // Only ever produced for a history row, so a live one should not reach here;
    // routed to the history shelf defensively rather than the actionable queue.
    case "fulfilled-unverified":
      return "fulfilled";
    case "declined":
      return "declined";
    case "pending":
    case "failed":
    case "unknown":
    default:
      return "awaiting";
  }
};

/** Partition requests onto shelves, preserving the incoming order within each. */
export const groupIntoShelves = (
  requests: EnrichedMediaRequest[],
): Map<RequestShelfId, EnrichedMediaRequest[]> => {
  const shelves = new Map<RequestShelfId, EnrichedMediaRequest[]>();
  for (const request of requests) {
    const id = shelfForRequest(request);
    const bucket = shelves.get(id);
    if (bucket) bucket.push(request);
    else shelves.set(id, [request]);
  }
  return shelves;
};

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** Epoch millis for a request's most recent movement, or 0 when it reports none. */
const requestTime = (request: MediaRequest): number => {
  const raw = request.updatedAt ?? request.requestedAt;
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Newest first.
 *
 * Undated requests sort last rather than first: a missing date is the provider
 * declining to say, and letting that read as "just now" would put the least
 * known requests at the top of a queue people work from the top of.
 */
export const byMostRecent = (a: MediaRequest, b: MediaRequest): number =>
  requestTime(b) - requestTime(a);

// ---------------------------------------------------------------------------
// The hero pick
// ---------------------------------------------------------------------------

/**
 * The request the surface leads with.
 *
 * The oldest thing awaiting a decision, because that is the one that has been
 * waiting longest on a person. Falls back to the most recent request of any
 * status so the stage is never empty on a board with no outstanding work — and
 * returns null only when there is genuinely nothing.
 *
 * Prefers a request that can actually be illustrated: the hero is a full-bleed
 * backdrop, and one with no artwork is a large empty rectangle.
 */
export const pickHeroRequest = (
  requests: EnrichedMediaRequest[],
): EnrichedMediaRequest | null => {
  if (requests.length === 0) return null;

  const awaiting = requests.filter((r) => shelfForRequest(r) === "awaiting");
  const pool = awaiting.length > 0 ? awaiting : requests;

  const illustrated = pool.filter(hasArtIdentifiers);
  const candidates = illustrated.length > 0 ? illustrated : pool;

  const sorted = [...candidates].sort(
    awaiting.length > 0
      ? (a, b) => requestTime(a) - requestTime(b) // oldest wait first
      : byMostRecent,
  );

  return sorted[0] ?? null;
};

// ---------------------------------------------------------------------------
// Library correlation (ghost cards)
// ---------------------------------------------------------------------------

/** A library item as far as request correlation is concerned. */
type CorrelatableItem = { tmdbId: string | null; tvdbId: string | null };

/**
 * The external ids present in a set of library items.
 *
 * Namespaced because a TMDb id and a TVDb id are different numbering schemes
 * that collide freely — matching on the bare number would call unrelated titles
 * the same title.
 */
export const buildLibraryIdSet = (items: CorrelatableItem[]): Set<string> => {
  const ids = new Set<string>();
  for (const item of items) {
    if (item.tmdbId) ids.add(`tmdb:${item.tmdbId}`);
    if (item.tvdbId) ids.add(`tvdb:${item.tvdbId}`);
  }
  return ids;
};

/**
 * Whether a request is already represented in a set of library items.
 *
 * Answers only what the ids can support. A request carrying no external id is
 * reported as not present, which is the safe direction: it shows as a ghost
 * that is genuinely in the library rather than silently vanishing.
 */
export const isRequestInLibrary = (
  request: MediaRequest,
  libraryIds: Set<string>,
): boolean => {
  if (request.tmdbId !== undefined && libraryIds.has(`tmdb:${request.tmdbId}`)) return true;
  if (request.tvdbId !== undefined && libraryIds.has(`tvdb:${request.tvdbId}`)) return true;
  return false;
};

/**
 * The requests that belong inline in a library view as ghosts.
 *
 * A ghost is a title someone asked for that is not here yet. Four things
 * disqualify a request from being one, and each is a different way of already
 * being answered:
 *
 * - `available` — the provider itself says it landed. This is the signal that
 *   works without a library join, and it is the provider's own word.
 * - a resolved library match — it has a real card, which wins.
 * - present among the items on screen — same, established by external id.
 * - `declined` — it is not coming, and leaving a denial sitting inline forever
 *   is clutter for the operator and a standing rejection for whoever asked.
 *   Denials live on the requests surface, not in the library.
 *
 * Restricted to the media type the view is showing, so a movie shelf does not
 * grow requested TV.
 */
export const selectGhostRequests = (
  requests: EnrichedMediaRequest[],
  libraryIds: Set<string>,
  mediaType?: MediaRequest["mediaType"],
): EnrichedMediaRequest[] => selectGhostBreakdown(requests, libraryIds, mediaType).ghosts;

/**
 * The same selection, with its arithmetic kept.
 *
 * A shorter list than the requests surface shows is correct but reads as two
 * screens disagreeing, so the counts that explain the difference are carried
 * out rather than discarded. Every request in scope lands in exactly one
 * bucket, so `ghosts + inLibrary + declined + otherType === considered`.
 */
export type GhostBreakdown = {
  ghosts: EnrichedMediaRequest[];
  /** Suppressed because the title is already on a server. */
  inLibrary: number;
  /** Suppressed because it was denied — not coming, so not "requested". */
  declined: number;
  /** Suppressed because this view shows one media type. */
  otherType: number;
  /** Everything considered, before any suppression. */
  considered: number;
};

export const selectGhostBreakdown = (
  requests: EnrichedMediaRequest[],
  libraryIds: Set<string>,
  mediaType?: MediaRequest["mediaType"],
): GhostBreakdown => {
  const ghosts: EnrichedMediaRequest[] = [];
  let inLibrary = 0;
  let declined = 0;
  let otherType = 0;

  for (const request of requests) {
    if (mediaType && request.mediaType !== mediaType) {
      otherType += 1;
      continue;
    }
    if (request.status === "declined") {
      declined += 1;
      continue;
    }
    // Three ways of already being here: the provider says so, the join resolved
    // it, or its external id is among the items on screen.
    if (
      request.status === "available" ||
      request.libraryMatch ||
      isRequestInLibrary(request, libraryIds)
    ) {
      inLibrary += 1;
      continue;
    }
    ghosts.push(request);
  }

  return { ghosts, inLibrary, declined, otherType, considered: requests.length };
};
